/**
 * @class UrlCampaignCodeUtility
 */
class UrlCampaignCodeUtility {
  /**
   * @param { String } search string
   * @returns { Object } query object
   */
  searchToQuery(search = '') {
    const query = {};
    const pairs = search && search.slice(1).split('&') || [];
    pairs.forEach(pair => {
      const tokens = pair.split('=');
      query[tokens[0]] = tokens[1];
    });
    return query;
  }

  /**
   * @param { Object } query query object
   * @returns { String } query string
   */
  queryToSearch(query = {}) {
    const pairs = (query && Object.keys(query) || {}).map(key => `${key}=${query[key]}`) || [];
    return pairs.length ? `?${pairs.join('&')}` : '';
  }

  /**
   * @param { Object } link link object
   * @returns { String } formatted url
   */
  formatUrl(link = {}) {
    const search = this.queryToSearch(link.query);
    return `${link.href}${search}${link.fragment}`;
  }

  /**
   * @param { String } url url string
   * @param { Object } regex regex object
   * @returns { String } matching string
   */
  getMatch(url, regex) {
    const matches = url.match(regex);
    return matches && matches.length > 1 && matches[1] || '';
  }

  /**
   * @param { String } url url string
   * @returns { Object } url & query object
   */
  parseUrl(url) {
    const href = this.getMatch(url, /^([^?#]+)/);
    const search = this.getMatch(url, /(\?[^#]+)/);
    const fragment = this.getMatch(url, /(#.+)$/);
    return {
      href,
      fragment,
      query: this.searchToQuery(search)
    };
  }

  /**
   * @param { Object } src src object
   * @param { Object } keys keys list
   * @returns { Object } src object
   */
  omit(src, keys) {
    const o = {};
    Object.keys(src).forEach(key => {
      if (keys.indexOf(key) === -1) {
        o[key] = src[key];
      }
    });
    return o;
  }

  /**
   * @param { Object } obj object to filter
   * @returns { Object } filtered object
   */
  filterNull(obj) {
    const filtered = {};
    Object.keys(obj || {}).forEach(key => {
      const value = obj[key];
      if (value) {
        filtered[key] = value;
      }
    });
    return Object.keys(filtered).length ? filtered : null;
  }

  /**
   * @param { Object } link link obj
   * @param { params } params params obj
   * @returns { Object } merged obj
   */
  mergeQuery(link, params) {
    return {
      ...link,
      query: {
        ...link.query,
        ...params
      }
    };
  }

  /**
   * @param { String } href href string
   * @returns { Boolean } is href valid?
   */
  validHref(href) {
    return href && href.indexOf('javascript:') === -1 // eslint-disable-line no-script-url
    && href.indexOf('tel:') === -1 && href.indexOf('#') !== 0;
  }

  /**
   * @method applyCampaignCodes
   * @param {String} href - the url to perform replacement on
   * @param {String} scsCampCode - sub-campaign code for external affiliates (overrides subCampCode)
   * @param {String} subCampCode - sub-campaign code for internal associates
   * @returns {String} URL that includes query parameters for any applicable sub-campaign codes
   */
  applyCampaignCodes(href, scsCampCode, subCampCode) {
    if (this.validHref(href)) {
      const link = this.parseUrl(href);
      if (scsCampCode) {
        return this.formatUrl(this.mergeQuery(link, {
          scsCampCode,
          subCampCode: scsCampCode
        }));
      }
      if (subCampCode) {
        return this.formatUrl(this.mergeQuery({
          ...link,
          query: this.omit(link.query, ['scsCampCode'])
        }, {
          subCampCode
        }));
      }
    }
    return href;
  }

  /**
   * Passing the nmls in all the URLs instead of affiliateCode
   * @param { String } href href string
   * @param { Object } responseBody response object
   * @param { String } prefix prefix string
   * @returns { Boolean } is href valid?
   */
  applyAffiliateCode(href, responseBody = {}, prefix = '') {
    const loanPurposeMap = {
      PropertyPurchase: 'purchase',
      Refinance: 'refinance',
      EquityRelease: 'homeEquity'
    };
    const response = {
      ...responseBody,
      loanPurpose: loanPurposeMap[responseBody.loanPurpose]
    };
    if (this.validHref(href)) {
      const linkUrl = `${prefix}${href}`;
      const {
        nmls
      } = response;
      const {
        affiliateCode
      } = response;
      let urlParams = null;
      if (nmls) urlParams = 'affiliateCode';else if (affiliateCode) urlParams = 'nmls';
      const {
        debugging
      } = response;
      const link = this.parseUrl(linkUrl);
      const hasApplyNow = /applynow/.test(linkUrl);
      const hasDmaDraDre = /apply-now-services/.test(linkUrl) && (/DMA/.test(linkUrl) || /DRA/.test(linkUrl) || /DME/.test(linkUrl));
      if (hasApplyNow || hasDmaDraDre) {
        const query = this.filterNull({
          ...this.omit(response, ['phoneNumber', urlParams, 'fName', 'lName', 'raw', 'debugging'])
        });
        return this.formatUrl(this.mergeQuery(link, query));
      }
      if (nmls) {
        return this.formatUrl(this.mergeQuery(link, this.filterNull({
          debugging,
          nmls
        })));
      } else if (affiliateCode) {
        return this.formatUrl(this.mergeQuery(link, this.filterNull({
          debugging,
          affiliateCode
        })));
      }
    }
    return href;
  }

  /**
   * @method applyDirectMarketingCode
   * @param {String} href - the url to perform replacement on
   * @param {String} dmcode - Direct Marketing code
   * @returns {String} A new URL that includes query parameters for direct marketing codes
   */
  applyDirectMarketingCode(href, dmcode) {
    const link = this.parseUrl(href);
    if (this.validHref(href) && dmcode) {
      const formattedUrl = this.formatUrl(this.mergeQuery(link, {
        dmcode
      }));
      return formattedUrl;
    }
    return href;
  }

  /**
   * @method applySourceCdParam
   * @param {String} href - the url to perform replacement on
   * @param {String} sourceCd  - sourceCd code
   * @returns {String} A new URL that includes query parameters for sourceCd code
   */
  applyDirectSourceCdParam(href, sourceCd) {
    const link = this.parseUrl(href);
    if (this.validHref(href) && sourceCd) {
      const formattedUrl = this.formatUrl(this.mergeQuery(link, {
        sourceCd
      }));
      return formattedUrl;
    }
    return href;
  }
}
var index = new UrlCampaignCodeUtility();

export { index as default };
